"use client";

import { useEffect, useRef, useState, type MouseEvent } from "react";
import { AnimatePresence, m } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { Container } from "@/components/ui/container";
import { LanguageSwitcher } from "@/components/language-switcher";
import { useI18n } from "@/components/i18n-provider";
import { business, nav } from "@/data/site-config";
import { localePath } from "@/lib/i18n";
import { cn } from "@/lib/utils";

export function Navbar() {
  const { locale, t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const pendingHash = useRef<string | null>(null);

  // Solid background once the page is scrolled. A plain passive listener keeps framer's
  // scroll tracking out of the initial bundle.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  function closeMenu() {
    document.body.style.overflow = "";
    setOpen(false);
  }

  // Section links in the mobile menu don't rely on the browser's default anchor
  // behaviour: that scroll starts while the menu is still collapsing and the page
  // is still scroll-locked, and it gets cancelled. Instead the tap only closes the
  // menu; once the collapse animation has finished (AnimatePresence
  // onExitComplete, with a timer as a fallback) we set the hash and scroll to the
  // section ourselves. Whichever fires first wins; the ref makes it run once.
  // Only on the home page: on the legal pages the links are ordinary navigations to
  // /<locale>/#section, which the browser handles.
  function onMenuLinkClick(e: MouseEvent<HTMLAnchorElement>, hash: string) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (!document.getElementById(hash.slice(1))) return;
    e.preventDefault();
    pendingHash.current = hash;
    closeMenu();
    window.setTimeout(scrollToPendingSection, 350);
  }

  function scrollToPendingSection() {
    const hash = pendingHash.current;
    pendingHash.current = null;
    if (!hash) return;
    const target = document.getElementById(hash.slice(1));
    if (!target) return;
    window.history.pushState(null, "", hash);
    target.scrollIntoView();
  }

  // Close the mobile menu on Escape, and lock body scroll while it's open.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-300",
        scrolled
          ? "bg-porcelain/85 shadow-[0_1px_0_0_var(--color-mist)] backdrop-blur-md"
          : "bg-transparent",
      )}
    >
      <Container className="flex h-16 items-center justify-between sm:h-20">
        <a href={localePath(locale, "#top")} className="flex items-center gap-2.5 font-display text-lg font-semibold text-ink">
          <svg width="26" height="26" viewBox="0 0 64 64" aria-hidden="true">
            <rect width="64" height="64" rx="16" fill="#142F2A" />
            <path
              d="M32 13c11 0 14.5 11 14.5 19S43 51 32 51 17.5 40 17.5 32 21 13 32 13Z"
              fill="#F3F5F1"
            />
            <circle cx="37.5" cy="22.5" r="3.4" fill="#C89448" />
          </svg>
          {business.name}
        </a>

        <nav className="hidden items-center gap-8 lg:flex" aria-label={t.a11y.primaryNav}>
          {nav.map((item) => (
            <a
              key={item.id}
              href={localePath(locale, item.hash)}
              className="font-mono text-xs font-medium uppercase tracking-wider text-ink-soft transition-colors hover:text-cusp"
            >
              {t.nav[item.id]}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <LanguageSwitcher />
          <a
            href={business.phoneHref}
            className="flex cursor-pointer items-center gap-2 rounded-full bg-cusp px-5 py-2.5 text-sm font-semibold text-porcelain transition-colors duration-200 hover:bg-cusp-deep"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            {business.phone}
          </a>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? t.a11y.closeMenu : t.a11y.openMenu}
          className="cursor-pointer p-2 text-ink lg:hidden"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </Container>

      <AnimatePresence onExitComplete={scrollToPendingSection}>
        {open && (
          <m.div
            id="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden border-t border-mist bg-porcelain lg:hidden"
          >
            <Container className="flex flex-col gap-1 py-4">
              {nav.map((item) => (
                <a
                  key={item.id}
                  href={localePath(locale, item.hash)}
                  onClick={(e) => onMenuLinkClick(e, item.hash)}
                  className="rounded-lg px-3 py-3 font-medium text-ink transition-colors hover:bg-cusp/5"
                >
                  {t.nav[item.id]}
                </a>
              ))}
              <div className="mt-2 border-t border-mist pt-3">
                <LanguageSwitcher variant="inline" onNavigate={closeMenu} />
              </div>
              <a
                href={business.phoneHref}
                onClick={closeMenu}
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-cusp px-5 py-3 text-center text-sm font-semibold text-porcelain"
              >
                <Phone className="h-4 w-4" aria-hidden="true" />
                {business.phone}
              </a>
            </Container>
          </m.div>
        )}
      </AnimatePresence>
    </header>
  );
}
